import { resolveTimeLeftData } from './time-left.utils';
import classNames from 'classnames';
import { useMemo, type FC, type ReactNode } from 'react';
import ClockPurple from 'src/assets/images/icons/default/ClockPurple';

export type TimeLeftLayout = 'compact-combined' | 'compact-with-left-suffix' | 'full-split' | 'full-split-with-left';

export interface TimeLeftProps {
  remainingMinutes?: number | null;
  timeLimitHours?: number | null;
  expiresOn?: string | null;
  compactLabel?: string | null;
  layout: TimeLeftLayout;
  className?: string;
  iconClassName?: string;
  valueClassName?: string;
  unitClassName?: string;
  gapClassName?: string;
  leftSuffix?: string;
  formatUnitWithLeft?: (unit: string) => string;
}

const TIME_LEFT_URGENT_TEXT_CLASS = '!text-content-negative-default';

const TimeLeft: FC<TimeLeftProps> = ({
  remainingMinutes,
  timeLimitHours,
  expiresOn,
  compactLabel,
  layout,
  className,
  iconClassName,
  valueClassName,
  unitClassName,
  gapClassName = 'gap-0.5',
  leftSuffix,
  formatUnitWithLeft,
}) => {
  const timeLeftData = useMemo(
    () =>
      resolveTimeLeftData({
        remainingMinutes,
        timeLimitHours,
        expiresOn,
        compactLabel,
      }),
    [compactLabel, expiresOn, remainingMinutes, timeLimitHours]
  );

  if (!timeLeftData) {
    return null;
  }

  const urgentTextClass = timeLeftData.isUrgent ? TIME_LEFT_URGENT_TEXT_CLASS : undefined;

  const getTextClassName = (baseClassName?: string) => classNames(baseClassName, urgentTextClass);

  const unitWithLeft =
    layout === 'full-split-with-left'
      ? (formatUnitWithLeft?.(timeLeftData.unit) ?? `${timeLeftData.unit} left`)
      : timeLeftData.unit;

  let textContent: ReactNode = null;

  switch (layout) {
    case 'compact-combined':
      textContent = <span className={getTextClassName(valueClassName)}>{timeLeftData.compactLabel}</span>;
      break;
    case 'compact-with-left-suffix':
      textContent = (
        <>
          <span className={getTextClassName(valueClassName)}>{timeLeftData.compactLabel}</span>
          {leftSuffix && <span className={getTextClassName(unitClassName)}>{leftSuffix}</span>}
        </>
      );
      break;
    case 'full-split':
      textContent = (
        <>
          <span className={getTextClassName(valueClassName)}>{timeLeftData.time}</span>
          <span className={getTextClassName(unitClassName)}>{timeLeftData.unit}</span>
        </>
      );
      break;
    case 'full-split-with-left':
      textContent = (
        <>
          <span className={getTextClassName(valueClassName)}>{timeLeftData.time}</span>
          <span className={getTextClassName(unitClassName)}>{unitWithLeft}</span>
        </>
      );
      break;
    default:
      textContent = null;
  }

  return (
    <div className={classNames('inline-flex items-center', gapClassName, className)}>
      <span className="inline-flex shrink-0 items-center justify-center leading-[0] -translate-y-px">
        <ClockPurple className={classNames('block shrink-0', iconClassName)} aria-hidden="true" />
      </span>
      <span className={classNames('inline-flex items-center leading-none', gapClassName)}>{textContent}</span>
    </div>
  );
};

export default TimeLeft;
